import { createStaffSettingsFeatureBundle } from "@triedge/staff-settings-ui/feature-bundle";
import { domainRouterClient } from "../api/domainRouterClient";
import { FINANCIAL_AID_REQUIREMENT_FIELDS } from "../financial-aid/financialAidRequirementFields";
import {
  buildAssistantSpreadsheetBridgeCallbackUri,
  clearAssistantSpreadsheetBridgeReturnContext,
  getAssistantSpreadsheetBridgeReturnContext,
  storeAssistantSpreadsheetBridgeCompletionContext,
  storeAssistantSpreadsheetBridgeReturnContext,
} from "../components/ai/assistantSpreadsheetBridgeClientState";
import { isAllowedAssistantWorkspaceGoogleRedirectUri } from "../../config/assistantWorkspaceGoogleOauthPolicy";
import { resolveCognitoProfile } from "../utils/cognito-profile";

const DEFAULT_MODULE_KEY = "staff_settings";

const toText = (value: unknown): string | null => {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
};

const compact = <T extends Record<string, unknown>>(input: T): T =>
  Object.fromEntries(
    Object.entries(input).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  ) as T;

const normalizeSettingsArgs = (
  args: { orgId?: string | null; moduleKey?: string | null } | void = {},
) => ({
  orgId: toText((args ?? {}).orgId) ?? "default",
  moduleKey: toText((args ?? {}).moduleKey) ?? DEFAULT_MODULE_KEY,
});

const toSettingsQueryInput = (
  args: { orgId?: string | null; moduleKey?: string | null } | void = {},
) => {
  const normalized = normalizeSettingsArgs(args);
  if (normalized.orgId === "default") {
    return { moduleKey: normalized.moduleKey };
  }
  return { orgId: normalized.orgId, moduleKey: normalized.moduleKey };
};

const toAssistantWorkflowStudioInput = (input: {
  workflowId: string;
  pathname?: string | null;
}): {
  workflowId: string;
  pathname?: string;
} => {
  const pathname = toText(input.pathname);
  return pathname
    ? { workflowId: input.workflowId, pathname }
    : { workflowId: input.workflowId };
};

const currentActorId = (): string | null =>
  resolveCognitoProfile()?.userSub ?? null;

export const settingsFeatureBundle = createStaffSettingsFeatureBundle({
  routeBase: "/school/settings",
  financialAidRequirementFields: FINANCIAL_AID_REQUIREMENT_FIELDS,
  resolveOperatorPersona: () =>
    resolveCognitoProfile()?.isSuperAdmin ? "super-administrator" : "admin",
  reducerPath: "settingsFeatureApi",
  settingsConfigAdapter: {
    getModuleConfig: (input) =>
      domainRouterClient.query("getModuleConfig", toSettingsQueryInput(input)),
    updateModuleConfig: (input) =>
      domainRouterClient.mutate("updateModuleConfig", {
        input: JSON.stringify(input),
      }),
    listStaffUsers: (input) =>
      domainRouterClient.query("listStaffUsers", input ?? {}),
    inviteStaffUser: (input) =>
      domainRouterClient.mutate("inviteStaffUser", {
        input: JSON.stringify(input),
      }),
    resendStaffInvite: (input) =>
      domainRouterClient.mutate("resendStaffInvite", {
        input: JSON.stringify(input),
      }),
    updateStaffUserRole: (input) =>
      domainRouterClient.mutate("updateStaffUserRole", {
        input: JSON.stringify(input),
      }),
    deactivateStaffUser: (input) =>
      domainRouterClient.mutate("deactivateStaffUser", {
        input: JSON.stringify(input),
      }),
    reactivateStaffUser: (input) =>
      domainRouterClient.mutate("reactivateStaffUser", {
        input: JSON.stringify(input),
      }),
    listStaffCodes: () => domainRouterClient.query("listStaffCodes", {}),
    createStaffCode: (input) =>
      domainRouterClient.mutate("createStaffCode", {
        input: JSON.stringify(input),
      }),
    rotateStaffCode: (input) =>
      domainRouterClient.mutate("rotateStaffCode", {
        input: JSON.stringify(input),
      }),
    revokeStaffCode: (input) =>
      domainRouterClient.mutate("revokeStaffCode", {
        input: JSON.stringify(input),
      }),
    listApprovedDomains: () =>
      domainRouterClient.query("listApprovedDomains", {}),
    addApprovedDomain: (input) =>
      domainRouterClient.mutate("addApprovedDomain", {
        input: JSON.stringify(input),
      }),
    removeApprovedDomain: (input) =>
      domainRouterClient.mutate("removeApprovedDomain", {
        input: JSON.stringify(input),
      }),
    listModuleEnablementAuditEvents: (input) =>
      domainRouterClient.query("listModuleEnablementAuditEvents", input ?? {}),
  },
  assistantSpreadsheetBridgeAdapter: {
    surfaceRoute: "/super-administrator/control-plane/integrations",
    getStatus: (input) =>
      domainRouterClient.query("getAssistantSpreadsheetBridgeStatus", {
        input: JSON.stringify(
          compact({
            tenantId: toText(input?.tenantId),
            userSub: toText(input?.userSub),
          }),
        ),
      }) as Promise<Record<string, unknown>>,
    startAuth: (input) =>
      domainRouterClient.mutate("startAssistantSpreadsheetBridgeAuth", {
        input: JSON.stringify(input),
      }) as Promise<Record<string, unknown>>,
    completeAuth: (input) =>
      domainRouterClient.mutate("completeAssistantSpreadsheetBridgeAuth", {
        input: JSON.stringify(input),
      }) as Promise<Record<string, unknown>>,
    exportToDrive: (input) =>
      domainRouterClient.mutate("exportAssistantSpreadsheetToGoogleDrive", {
        input: JSON.stringify(input),
      }) as Promise<Record<string, unknown>>,
    disconnect: (input) =>
      domainRouterClient.mutate("disconnectAssistantSpreadsheetBridge", {
        input: JSON.stringify(input ?? {}),
      }) as Promise<Record<string, unknown>>,
    savePreset: (input) =>
      domainRouterClient.mutate("saveAssistantSpreadsheetExportPreset", {
        input: JSON.stringify(input),
      }) as Promise<Record<string, unknown>>,
    deletePreset: (input) =>
      domainRouterClient.mutate("deleteAssistantSpreadsheetExportPreset", {
        input: JSON.stringify(input),
      }) as Promise<Record<string, unknown>>,
    buildCallbackUri: (input) =>
      buildAssistantSpreadsheetBridgeCallbackUri(input),
    isRedirectUriAllowed: (redirectUri) =>
      isAllowedAssistantWorkspaceGoogleRedirectUri(redirectUri),
    getReturnContext: () => getAssistantSpreadsheetBridgeReturnContext(),
    storeReturnContext: (context) =>
      storeAssistantSpreadsheetBridgeReturnContext(context),
    clearReturnContext: () => clearAssistantSpreadsheetBridgeReturnContext(),
    storeCompletionContext: (context) =>
      storeAssistantSpreadsheetBridgeCompletionContext(context),
  },
  assistantWorkflowStudioAdapter: {
    getStudioState: (input) =>
      domainRouterClient.query(
        "getAssistantWorkflowStudioState",
        toAssistantWorkflowStudioInput(input),
      ),
    upsertDraft: (input) =>
      domainRouterClient.mutate("upsertAssistantWorkflowDraft", { input }),
    submitForApproval: (input) =>
      domainRouterClient.mutate("submitAssistantWorkflowForApproval", {
        input,
      }),
    reviewWorkflow: (input) =>
      domainRouterClient.mutate("reviewAssistantWorkflow", { input }),
    mutateRun: (input) =>
      domainRouterClient.mutate("mutateAssistantRun", { input }),
  },
  paymentControlPlaneAdapter: {
    getReadiness: (input) =>
      domainRouterClient.query("getPaymentControlPlaneReadiness", {
        input: JSON.stringify(
          compact({
            tenantId: toText(input?.tenantId),
          }),
        ),
      }),
    updateControlPlane: (input) =>
      domainRouterClient.mutate("updatePaymentControlPlane", {
        input,
      }),
    startConnectedAccountOnboarding: (input) =>
      domainRouterClient.mutate("startStripeConnectedAccountOnboarding", {
        input: JSON.stringify(input),
      }),
    completeConnectedAccountOnboarding: (input) =>
      domainRouterClient.mutate("completeStripeConnectedAccountOnboarding", {
        input: JSON.stringify(input),
      }),
  },
  financialAidHistoricalImportAdapter: {
    getTemplate: (input) =>
      domainRouterClient.query(
        "getFinancialAidHistoricalImportTemplate",
        toSettingsQueryInput(input),
      ),
    getStatus: (input) =>
      domainRouterClient.query(
        "getFinancialAidHistoricalImportStatus",
        compact({
          ...toSettingsQueryInput(input),
          importId: toText(input?.importId),
        }),
      ),
    requestUpload: (input) =>
      domainRouterClient.mutate("requestFinancialAidHistoricalImportUpload", {
        input: JSON.stringify(input),
      }),
    startImport: (input) =>
      domainRouterClient.mutate("startFinancialAidHistoricalImport", {
        input: JSON.stringify(input),
      }),
  },
});

export const resolveSettingsActorId = (): string | null => currentActorId();
